import {css} from '@emotion/react';
import {useTheme} from '@mui/material';
import {asQueryString, getIsLinkActive, GetStylesFunction, RemoveButton, TranslateUtils, useMuiAnchor} from '@wallaby-ui/core';
import classNames from 'classnames';
import * as React from 'react';
import {defineMessages, IntlShape} from 'react-intl';
import {NavLink} from 'react-router-dom';

import {REMOVE_ITEM_RIGHT_OFFSET} from './NavigationHeader';
import styles from './NavigationItem.less';
import {NavigationItemIcon} from './NavigationItemIcon';
import {DynamicItem, DynamicItemId, NavigationContext, Route} from '../useNavigationContext';
import {JustTooltipFormatter} from '../../formatters';
import {AG_GRID_COMPATIBILITY} from '../../LightTable';
import {useCallback, useRef} from 'react';
// import {GLOBAL_PARAMETERS_PATH} from 'src/js/containers/location';

const I18N = defineMessages({
  REMOVE_BUTTON: {
    id: 'navigation.button.remove',
    defaultMessage: 'Close',
    description: 'text for navigation remove button',
  },
});

interface OwnProps {
  route: Route;
  currentPathName?: string;
  isShrank?: boolean;
  navigationContext: NavigationContext;
  intl: IntlShape;
  removeDynamicItem?: (itemToDelete: DynamicItemId) => void;
  isTooltipInNavigationMenu?: boolean;
  isNavigationOpen?: boolean;
}

type Props = OwnProps;

export const NavigationItem: React.FC<Props> = (props) => {
  const {route, currentPathName, isShrank, navigationContext, intl, removeDynamicItem, isTooltipInNavigationMenu, isNavigationOpen} = props;
  const {path: _path, iconName: _iconName, iconClass: _iconClass, iconViewbox: _iconViewbox} = route;
  const {dynamicItem} = route;
  const isPatient = Boolean(dynamicItem?.isPatient);
  const label = dynamicItem?.label ?? TranslateUtils.translate(route.label ?? '', intl);

  const theme = useTheme();
  const hasTooltip = !!label;

  const {mouseEventEnter, mouseEventLeave, open, anchorEl} = useMuiAnchor(hasTooltip);
  const navigationItemStyle = getStyles({theme});

  const indexPath = navigationContext.navConfig.index ?? '/';
  const pathWithParams = `${_path}${dynamicItem?.paramsString ?? ''}`;
  const isLinkActive = getIsLinkActive(pathWithParams, dynamicItem?.isPrefix, currentPathName ?? '', indexPath);
  const isDynamic = Boolean(removeDynamicItem);
  const haveDynamicIcon = Boolean(dynamicItem?.iconName ?? dynamicItem?.iconClass);
  const iconName = haveDynamicIcon ? dynamicItem?.iconName : _iconName;
  const iconClass = haveDynamicIcon ? dynamicItem?.iconClass : _iconClass;
  const iconViewbox = haveDynamicIcon ? dynamicItem?.iconViewbox : _iconViewbox;
  const queryString = dynamicItem?.query ? `?${asQueryString(dynamicItem.query)}` : '';
  const fullPath = `${pathWithParams}${queryString}`;
  return (
    <div className={styles.dlNavigationItemWrapper}>
      <NavLink
        className={({isActive: _}) => {
          return classNames({
            [styles.dlNavigationItem]: true,
            [styles.dlNavigationItemActive]: isLinkActive,
            [styles.dlCenter]: isShrank,
            [styles.dlNonDynamicItem]: !isDynamic,
            [styles.dlDynamicItem]: isDynamic,
            [styles.dlDynamicItemPadding]: removeDynamicItem && !isShrank,
          });
        }}
        tabIndex={0}
        onMouseEnter={mouseEventEnter}
        onMouseLeave={mouseEventLeave}
        to={fullPath}
      >
        <JustTooltipFormatter
          {...AG_GRID_COMPATIBILITY}
          popperStyle={navigationItemStyle.popperStyle}
          valueStyle={isShrank ? {display: 'flex', justifyContent: 'center'} : undefined}
          value={
            isShrank ? (
              <NavigationItemIcon
                iconName={iconName}
                iconClass={iconClass}
                iconViewbox={iconViewbox}
                isPatient={isPatient}
                avatarText={label}
                navigationMenuOpen
                isDynamic={isDynamic}
                isActive={isLinkActive}
              />
            ) : (
              <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                <NavigationItemIcon
                  iconName={iconName}
                  iconClass={iconClass}
                  iconViewbox={iconViewbox}
                  isPatient={isPatient}
                  avatarText={label}
                  navigationMenuOpen={false}
                  isDynamic={isDynamic}
                  isActive={isLinkActive}
                />
                <span
                  className={classNames(styles.dlNavigationItemLabel, {
                    [styles.dlNavigationItemLabelSecondary]: isDynamic,
                  })}
                  style={{marginTop: '2px'}}
                >
                  {label}
                </span>
              </div>
            )
          }
          tooltipOverride={label}
          isTooltipInNavigationMenu={isTooltipInNavigationMenu}
          isNavigationOpen={isNavigationOpen}
        />
      </NavLink>
      {isDynamic && removeDynamicItem && !isShrank ? (
        <span
          data-name={'navigation-item--remove-item-wrapper'}
          css={navigationItemStyle.removeItem}
          className={classNames(styles.dlRemoveItemPadding)}
        >
          <RemoveButton
            className={styles.dlRemoveItemButton}
            item={route}
            removeCallback={() => {
              removeDynamicItem(route.dynamicItem as DynamicItem);
            }}
            titleText={I18N.REMOVE_BUTTON.id}
          />
        </span>
      ) : null}
      {/*
      <DlMuiPopper hasTooltip={hasTooltip} anchorEl={anchorEl} open={open}>
        <Typography variant={'popper'}>{translatedLabel}</Typography>
      </DlMuiPopper>
      */}
    </div>
  );
};

NavigationItem.displayName = 'NavigationItem';

const getStyles: GetStylesFunction = (options) => ({
  removeItem: css({
    right: REMOVE_ITEM_RIGHT_OFFSET,
    color: `${options?.theme?.palette.custom.removeItemIcon}`,
    fontSize: 'var(--dlFontSizeNormal)',
    position: 'absolute',
    top: '50%',
    transform: 'translate(0, -50%)',
  }),

  popperStyle: css`
    pointer-events: none;
  `,
});
